<script setup>
// @ts-check

import CreateEntryButton from '@/components/entry/CreateEntryButton.vue'
import ReferenceDatatablePreview from '@/components/references/ReferenceDatatablePreview.vue'
import SalarySummary from '@/components/salaries/SalarySummary.vue'
import SalaryDatatable from '@/components/salaries/tables/SalaryDatatable.vue'
import ServerDatatableControls from '@/components/tables/ServerDatatableControls.vue'
import TableWithPreview from '@/components/tables/TableWithPreview.vue'
import { payslipInputStatuses } from '@/constants/salaries'
import { usePayslipInputActions } from '@/core/composables/salaries/usePayslipInputActions'
import { computed, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

defineOptions({
  name: 'SalaryIndex'
})

const { t } = useI18n()

const isLoading = ref(true)

const datatable = useTemplateRef('datatable')

/** @type {import('vue').Ref<?ReferenceInterface>} */
const payslipInputReference = ref(null)

/** @type {import('vue').ComputedRef<FilterItem[]>} */
const filters = computed(() =>
  payslipInputStatuses.map((status) => ({
    label: t(`references.payslip_input.status.${status}`),
    param: 'state[]',
    value: status
  }))
)

/**
 * @param {?ReferenceInterface} inputReference
 */
const selectPayslipInput = async (inputReference) => {
  payslipInputReference.value = inputReference
}

const onUpdate = () => {
  payslipInputReference.value = null
  // @ts-expect-error - Requires TypeScript support
  datatable.value.refresh()
}

const {
  navigateToEditPayslipInput,
  showPayslipInputDeletionDialog,
  showPayslipInputInvalidationDialog,
  showPayslipInputPaymentDialog
} = usePayslipInputActions({
  onUpdate
})
</script>

<template>
  <ServerDatatableControls :disabled="isLoading" :filters>
    <template #header>
      <CreateEntryButton class="flex-1 sm:flex-none" type="salary" />
    </template>
  </ServerDatatableControls>

  <TableWithPreview
    class="my-3"
    @invalidate="showPayslipInputInvalidationDialog"
    @edit="navigateToEditPayslipInput"
    @delete="showPayslipInputDeletionDialog"
    @mark-as-paid="showPayslipInputPaymentDialog"
    @close="selectPayslipInput(null)"
  >
    <template #table="tableProps">
      <SalaryDatatable
        v-bind="tableProps"
        ref="datatable"
        v-model:loading="isLoading"
        :use-mobile="!!payslipInputReference"
        :selected="payslipInputReference?.id"
        @select="selectPayslipInput"
      />
    </template>

    <template #close>
      {{ t('entries.back_to_list_type.salaries') }}
    </template>

    <template v-if="payslipInputReference" #preview="previewProps">
      <ReferenceDatatablePreview
        v-bind="previewProps"
        :reference="payslipInputReference"
      >
        <template #preview="{ preview, reference }">
          <div class="pt-4">
            <SalarySummary
              v-if="preview"
              class="p-0!"
              :payslip-input="preview"
              secured
              :disabled="!reference.actions.can_edit"
            />
            <div v-else>
              <VSkeletonLoader v-for="i in 3" :key="i" type="card" />
            </div>
          </div>
        </template>
      </ReferenceDatatablePreview>
    </template>
  </TableWithPreview>
</template>
